import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { environment } from '@env/environment';
import { Observable, tap } from 'rxjs';
import { UserSessionDTO } from './userSessionDTO';

interface JwtClaims {
  exp?: number;
  permissions?: string[] | string;
  roles?: string[] | string;
  scope?: string[] | string;
  sub?: string;
  id?: string;
  person_id?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AuthSession {
  accessToken: string;
  provider: string;
  user: UserSessionDTO | null;
  permissions: string[];
  roles: string[];
  scopes: string[];
  expiresAt: number | null;
}

const TOKEN_STORAGE_KEY = 'facilchat.access_token';
const PROVIDER_STORAGE_KEY = 'facilchat.provider';
const USER_STORAGE_KEY = 'facilchat.user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly sessionState = signal<AuthSession | null>(this.restoreSession());

  readonly session = this.sessionState.asReadonly();
  readonly user = computed(() => this.sessionState()?.user ?? null);

  redirectToAuth(redirectUrl: string = '/'): void {
    const normalizedTargetUrl = this.normalizeTargetUrl(redirectUrl);
    sessionStorage.setItem('facilchat.target_url', normalizedTargetUrl);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: environment.clientId,
      redirect_uri: environment.redirectUri,
      scope: environment.scope
    });
    window.location.href = `${environment.authUrl}/oauth2/authorize?${params.toString()}`;
  }

  handleCallback(code: string): Observable<any> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', environment.clientId)
      .set('client_secret', environment.clientSecret)
      .set('redirect_uri', environment.redirectUri)
      .set('code', code);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(`${environment.authUrl}/oauth2/token`, body.toString(), { headers }).pipe(
      tap((response) => {
        if (response.access_token) {
          this.setToken(response.access_token);
        }
        if (response.refresh_token) {
          sessionStorage.setItem('facilchat.refresh_token', response.refresh_token);
        }

        const targetUrl = sessionStorage.getItem('facilchat.target_url') || '/';
        sessionStorage.removeItem('facilchat.target_url');
        this.router.navigateByUrl(targetUrl);
      })
    );
  }

  private normalizeTargetUrl(targetUrl: string): string {
    if (!targetUrl || targetUrl === '/callback' || targetUrl.startsWith('/callback?')) {
      return '/dashboard';
    }
    return targetUrl;
  }

  setToken(token: string, provider = 'oidc'): void {
    const session = this.buildSession(token, provider);
    if (!session) {
      this.clearSession();
      return;
    }

    this.persistSession(session);
    this.sessionState.set(session);
  }

  getToken(): string | null {
    const session = this.sessionState();
    if (!session) {
      return null;
    }

    if (this.isSessionExpired(session)) {
      this.clearSession();
      return null;
    }

    return session.accessToken;
  }

  getUser(): UserSessionDTO | null {
    return this.user();
  }

  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded?.exp) {
      return false;
    }

    return decoded.exp <= Math.floor(Date.now() / 1000);
  }

  isAuthenticated(): boolean {
    const session = this.sessionState();
    if (!session) {
      return false;
    }

    if (this.isSessionExpired(session)) {
      this.clearSession();
      return false;
    }

    return true;
  }

  logout(): void {
    this.clearSession();
    void this.router.navigate(['/session-required'], {
      queryParams: { reason: 'logout' }
    });
  }

  hasStoredToken(): boolean {
    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    return Boolean(storedToken && storedToken !== 'undefined');
  }

  hasPermission(permission: string | string[]): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    const requiredPermissions = this.toArray(permission);
    if (requiredPermissions.length === 0) {
      return true;
    }

    const session = this.sessionState();
    if (!session) {
      return false;
    }

    return requiredPermissions.some(item =>
      session.permissions.includes(item) || session.scopes.includes(item)
    );
  }

  hasRole(role: string | string[]): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    const requiredRoles = this.toArray(role);
    if (requiredRoles.length === 0) {
      return true;
    }

    const session = this.sessionState();
    if (!session) {
      return false;
    }

    return requiredRoles.some(item => session.roles.includes(item));
  }

  handleSessionExpired(redirectUrl?: string): void {
    this.clearSession();
    void this.router.navigate(['/session-required'], {
      queryParams: {
        reason: 'expired',
        redirect: redirectUrl ?? this.router.url
      }
    });
  }

  handleUnauthorized(redirectUrl?: string): void {
    void this.router.navigate(['/access-denied'], {
      queryParams: {
        redirect: redirectUrl ?? this.router.url
      }
    });
  }

  private restoreSession(): AuthSession | null {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const provider = sessionStorage.getItem(PROVIDER_STORAGE_KEY) ?? 'oidc';

    if (!token || token === 'undefined') {
      return null;
    }

    const session = this.buildSession(token, provider);
    if (!session || this.isSessionExpired(session)) {
      this.clearSessionStorage();
      return null;
    }

    return session;
  }

  private buildSession(token: string, provider: string): AuthSession | null {
    const claims = this.decodeToken(token);
    if (!claims) {
      return null;
    }

    return {
      accessToken: token,
      provider,
      user: this.buildUser(claims, provider),
      permissions: this.normalizeClaimValue(claims.permissions),
      roles: this.normalizeClaimValue(claims.roles),
      scopes: this.normalizeClaimValue(claims.scope),
      expiresAt: claims.exp ? claims.exp * 1000 : null
    };
  }

  private buildUser(claims: JwtClaims, provider: string): UserSessionDTO | null {
    const id = String(claims.id ?? claims.sub ?? claims.person_id ?? '');
    const givenName = claims.given_name ?? '';
    const familyName = claims.family_name ?? '';
    const fullName = claims.name ?? [givenName, familyName].filter(Boolean).join(' ').trim();
    const email = claims.email ?? '';

    if (!id && !email && !fullName) {
      return null;
    }

    return {
      id,
      given_name: givenName,
      family_name: familyName,
      name: fullName || email || 'Usuario',
      email,
      picture: claims.picture ?? '',
      provider
    };
  }

  private decodeToken(token: string): JwtClaims | null {
    try {
      return jwtDecode<JwtClaims>(token);
    } catch {
      return null;
    }
  }

  private normalizeClaimValue(value: string[] | string | undefined): string[] {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (typeof value === 'string') {
      return value
        .split(/[\s,]+/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  private toArray(value: string | string[]): string[] {
    return Array.isArray(value) ? value : [value];
  }

  private isSessionExpired(session: AuthSession): boolean {
    return session.expiresAt !== null && session.expiresAt <= Date.now();
  }

  private persistSession(session: AuthSession): void {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, session.accessToken);
    sessionStorage.setItem(PROVIDER_STORAGE_KEY, session.provider);

    if (session.user) {
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(session.user));
      return;
    }

    sessionStorage.removeItem(USER_STORAGE_KEY);
  }

  private clearSession(): void {
    this.clearSessionStorage();
    this.sessionState.set(null);
  }

  private clearSessionStorage(): void {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(PROVIDER_STORAGE_KEY);
    sessionStorage.removeItem(USER_STORAGE_KEY);
    sessionStorage.removeItem('facilchat.refresh_token');
    sessionStorage.removeItem('facilchat.target_url');
  }
}
